import {
  cabinBaggage,
  checkInBaggage,
  flexibleTicket,
  meal,
  secondBaggagePerTraveler,
  seatMap,
  withParseAvailability,
} from '@eti/products';
import type { useTranslation } from '@eti/providers';
import type { BoundType, CartType, CartTypePaymentLink, Travelers } from '../../models';

type Product = NonNullable<NonNullable<CartType['products']>[number]>;

export type BuySpecification = NonNullable<Product['buySpecification']>;

type GetTranslation = ReturnType<typeof useTranslation>['t'];

const nonNullable = <T>(value: T): value is NonNullable<T> => Boolean(value);

const travelerProducts: number[] = [
  cabinBaggage.id,
  checkInBaggage.id,
  flexibleTicket.id,
  meal.id,
  secondBaggagePerTraveler.id,
  seatMap.id,
];

export const hasAvailability = (
  productSpecificTexts?: Record<string, unknown> | null,
): productSpecificTexts is { productSegmentAvailabilityTexts: string } =>
  Boolean(productSpecificTexts?.productSegmentAvailabilityTexts);

export const getNumberOfUnits = (buySpecification?: BuySpecification | null) => {
  if (!buySpecification) {
    return 0;
  }
  if ('travelerPrices' in buySpecification) {
    return buySpecification.travelerPrices.length;
  }

  if ('travelerChoicePrices' in buySpecification) {
    return buySpecification.travelerChoicePrices.length;
  }

  if ('travelerBaggageTripPrices' in buySpecification) {
    return buySpecification.travelerBaggageTripPrices.filter(
      (el, i, arr) => arr.findIndex((obj) => obj?.travelerId === el?.travelerId) === i,
    ).length;
  }

  if ('travelerSeatMapPrices' in buySpecification) {
    return buySpecification.travelerSeatMapPrices.length;
  }
  return 0;
};

export const getTravelerSeatMapDetails = (
  travelerSeatMapDetails: Extract<
    BuySpecification,
    { travelerSeatMapDetails: unknown[] }
  >['travelerSeatMapDetails'],
  travelers: Travelers,
) =>
  travelers.map((traveler) => {
    const seatDetails = (travelerSeatMapDetails ?? [])
      .filter((seatMapTraveler) => String(seatMapTraveler?.travelerId) === String(traveler.id))
      .filter(nonNullable);

    if (seatDetails.length > 0) {
      const { priceRaw, markup } = seatDetails.reduce(
        (total, { price }) => ({
          priceRaw: total.priceRaw + (price?.price?.value || 0),
          markup: total.markup + (price?.markup?.value || 0),
        }),
        { priceRaw: 0, markup: 0 },
      );

      return {
        ancillaryCarrierPriceBreakdowns: seatDetails[0]?.ancillaryCarrierPriceBreakdowns ?? [],
        travelerId: traveler.id,
        priceRaw,
        markup,
      };
    }

    return [];
  });

export const getTravelers = (
  buySpecification: BuySpecification | null | undefined,
  travelers: Travelers,
  productId: number,
) => {
  if (!buySpecification) {
    return [];
  }
  if (travelerProducts.includes(productId)) {
    if ('travelerPrices' in buySpecification) {
      return buySpecification.travelerPrices;
    }

    if ('travelerChoicePrices' in buySpecification) {
      return buySpecification.travelerChoicePrices;
    }

    if ('travelerSeatMapDetails' in buySpecification) {
      return getTravelerSeatMapDetails(buySpecification.travelerSeatMapDetails, travelers);
    }

    if ('travelerCabinBaggagePrices' in buySpecification) {
      return travelers.map((traveler) => {
        const baggageDetails = buySpecification.travelerCabinBaggagePrices
          .filter((product) => product?.travelerId === traveler.id)
          .filter(nonNullable);
        if (baggageDetails.length > 0) {
          return {
            ancillaryCarrierPriceBreakdowns: baggageDetails[0]?.ancillaryCarrierPriceBreakdowns,
            travelerId: traveler.id,
            priceRaw: baggageDetails.reduce((total, { price }) => total + price.price.value, 0),
            markup: baggageDetails.reduce(
              (total, { price }) => total + (price.markup?.value ?? 0),
              0,
            ),
          };
        }
        return [];
      });
    }

    if ('travelerBaggageTripPrices' in buySpecification) {
      return travelers.map((traveler) => {
        const baggageDetails = buySpecification.travelerBaggageTripPrices
          .filter((product) => product?.travelerId === traveler.id)
          .filter(nonNullable);

        if (baggageDetails.length > 0) {
          return {
            ancillaryCarrierPriceBreakdowns: baggageDetails[0].ancillaryCarrierPriceBreakdowns,
            travelerId: traveler.id,
            priceRaw: baggageDetails.reduce((total, { price }) => total + price.price?.value, 0),
            markup: baggageDetails.reduce(
              (total, { price }) => total + (price.markup?.value ?? 0),
              0,
            ),
            pieces: baggageDetails[0].pieces,
            maxWeight: baggageDetails[0].maxWeight,
            weightUnit: baggageDetails[0].weightUnit,
          };
        }
        return [];
      });
    }
  }
  return [];
};

export const transformProducts = (
  getTranslation: GetTranslation,
  products: NonNullable<CartType['products']> = [],
  travelers: Travelers = [],
  bounds: BoundType[] = [],
) => {
  if (!products) {
    return [];
  }

  return products
    .filter(nonNullable)
    .map(
      ({
        productId,
        name,
        price,
        buySpecification,
        productSummaryAlternativeName,
        code,
        productSpecificTexts,
        ...rest
      }) => {
        const numberOfUnits = getNumberOfUnits(buySpecification);
        const transformedTravelers = getTravelers(buySpecification, travelers, Number(productId));

        const parseAvailability = withParseAvailability(
          Number(productId),
          getTranslation,
          `${name.toLowerCase()}GeneralPurpose`,
        );

        return {
          id: productId,
          code,
          title: productSummaryAlternativeName || name,
          price: price.price.value,
          markup: price.markup && price.markup.value,
          numberOfUnits,
          travelers: transformedTravelers,
          availability: hasAvailability(productSpecificTexts)
            ? parseAvailability(
                productSpecificTexts?.productSegmentAvailabilityTexts as any,
                bounds as any,
                travelers.length,
              )
            : null,
          ...rest,
        };
      },
    );
};

export type TransformedProduct = ReturnType<typeof transformProducts>[number];

export const transformPaymentLinkProducts = (products?: CartTypePaymentLink['products']) => {
  if (!products) {
    return [];
  }

  return products
    .filter(nonNullable)
    .map(({ code, productId, name, price, text, travelers, productSummaryAlternativeName }) => ({
      code,
      id: productId,
      title: productSummaryAlternativeName || name,
      price: price.price.value,
      markup: price.markup && price.markup.value,
      text,
      travelers,
    }));
};

export type TransformedPaymentLinkProduct = ReturnType<typeof transformPaymentLinkProducts>[number];
